import {
  ArrowLeftOutlined
} from '@ant-design/icons';
import { Button, Card, Col, Pagination, Row } from 'antd';
import React from 'react';
import { useI18n } from '../i18n';
import '../styles/ClassroomPage.css';
import '../styles/common.css';



interface Article {
  id: string;
  title: string;
  badge: string;
  subtitle: string;
  content: string;
}

const ClassroomPage: React.FC = () => {
  const { t } = useI18n();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentArticle, setCurrentArticle] = React.useState<Article | null>(null);

  // 文章数据 - 使用翻译系统，使用 useMemo 确保语言切换时重新生成
  const articles: Article[] = React.useMemo(() => [
    {
      id: 'covid-nasal-rinse-guide',
      title: t('classroom.articles.covid.title'),
      badge: t('classroom.articles.covid.badge'),
      subtitle: t('classroom.articles.covid.subtitle'),
      content: t('classroom.articles.covid.content')
    },
    {
      id: 'cold-nasal-rinse',
      title: t('classroom.articles.cold.title'),
      badge: t('classroom.articles.cold.badge'),
      subtitle: t('classroom.articles.cold.subtitle'),
      content: t('classroom.articles.cold.content')
    },
    {
      id: 'rhinitis-nasal-rinse',
      title: t('classroom.articles.rhinitis.title'),
      badge: t('classroom.articles.rhinitis.badge'),
      subtitle: t('classroom.articles.rhinitis.subtitle'),
      content: t('classroom.articles.rhinitis.content')

    },
    {
      id: 'allergic-rhinitis-comprehensive',
      title: t('classroom.articles.allergicRhinitis.title'),
      badge: t('classroom.articles.allergicRhinitis.badge'),
      subtitle: t('classroom.articles.allergicRhinitis.subtitle'),
      content: t('classroom.articles.allergicRhinitis.content')

    },
    {
      id: 'rhinitis-sinusitis-difference',
      title: t('classroom.articles.rhinitisSinusitisDifference.title'),
      badge: t('classroom.articles.rhinitisSinusitisDifference.badge'),
      subtitle: t('classroom.articles.rhinitisSinusitisDifference.subtitle'),
      content: t('classroom.articles.rhinitisSinusitisDifference.content')
    },
    {
      id: 'rhinitis-medication-guide',
      title: t('classroom.articles.rhinitisMedicationGuide.title'),
      badge: t('classroom.articles.rhinitisMedicationGuide.badge'),
      subtitle: t('classroom.articles.rhinitisMedicationGuide.subtitle'),
      content: t('classroom.articles.rhinitisMedicationGuide.content')
    },
    {
      id: 'nasal-irrigator-selection-guide',
      title: t('classroom.articles.nasalIrrigatorSelectionGuide.title'),
      badge: t('classroom.articles.nasalIrrigatorSelectionGuide.badge'),
      subtitle: t('classroom.articles.nasalIrrigatorSelectionGuide.subtitle'),
      content: t('classroom.articles.nasalIrrigatorSelectionGuide.content')
    },
    {
      id: 'saline-solution-guide',
      title: t('classroom.articles.salineSolutionGuide.title'),
      badge: t('classroom.articles.salineSolutionGuide.badge'),
      subtitle: t('classroom.articles.salineSolutionGuide.subtitle'),
      content: t('classroom.articles.salineSolutionGuide.content')
    },
    {
      id: 'allergic-rhinitis-care',
      title: t('classroom.articles.allergicRhinitisCare.title'),
      badge: t('classroom.articles.allergicRhinitisCare.badge'),
      subtitle: t('classroom.articles.allergicRhinitisCare.subtitle'),
      content: t('classroom.articles.allergicRhinitisCare.content')
    },
    {
      id: 'sinusitis-prevention-treatment',
      title: t('classroom.articles.sinusitisPreventionTreatment.title'),
      badge: t('classroom.articles.sinusitisPreventionTreatment.badge'),
      subtitle: t('classroom.articles.sinusitisPreventionTreatment.subtitle'),
      content: t('classroom.articles.sinusitisPreventionTreatment.content')
    },
    {
      id: 'seasonal-rhinitis-strategies',
      title: t('classroom.articles.seasonalRhinitisStrategies.title'),
      badge: t('classroom.articles.seasonalRhinitisStrategies.badge'),
      subtitle: t('classroom.articles.seasonalRhinitisStrategies.subtitle'),
      content: t('classroom.articles.seasonalRhinitisStrategies.content')
    },
    {
      id: 'children-nasal-care-guide',
      title: t('classroom.articles.childrenNasalCareGuide.title'),
      badge: t('classroom.articles.childrenNasalCareGuide.badge'),
      subtitle: t('classroom.articles.childrenNasalCareGuide.subtitle'),
      content: t('classroom.articles.childrenNasalCareGuide.content')
    }
  ], [t]);

  // 分页配置
  const pageSize = 6;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentArticles = articles.slice(startIndex, endIndex);

  const handleArticleClick = (article: Article) => {
    setCurrentArticle(article);
  };

  const handleBackToList = () => {
    setCurrentArticle(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 当语言切换时，更新当前文章内容
  React.useEffect(() => {
    if (currentArticle) {
      const updatedArticle = articles.find(article => article.id === currentArticle.id);
      if (updatedArticle) {
        setCurrentArticle(updatedArticle);
      }
    }
  }, [articles, currentArticle]);

  // 样式移动到独立的 CSS 文件中
  if (currentArticle) {
    return (
      <div className="classroom-page no-nav-offset">
        <div className="article-detail">
          <div className="article-header">
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
              <Row justify="center">
                <Col xs={24} lg={16} style={{ textAlign: 'center' }}>
                  <h1 className="display-4 font-weight-bold mb-4">{currentArticle.title}</h1>
                  <p className="lead mb-0">{currentArticle.subtitle}</p>
                </Col>
              </Row>
            </div>
          </div>
        </div>

        <div className="article-content">
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
            <Row justify="center">
              <Col xs={24} lg={16}>
                <Button
                  type="default"
                  className="back-btn btn-secondary-unified"
                  onClick={handleBackToList}
                  icon={<ArrowLeftOutlined />}
                >
                  {t('classroom.backToList')}
                </Button>

                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: currentArticle.content }}
                />
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="classroom-page no-nav-offset">
      {/* 页面标题 */}
      <section style={{ width: '100%', lineHeight: 0, margin: 0, padding: 0 }}>
        <img src={'/img/phone/26.jpg'} style={{ width: '100%', display: 'block', margin: 0 }} alt="spacing" />
      </section>

      {/* 主要内容 */}
      <div className="container py-5">
        <Row>
          <Col span={24}>
            <h2 className="section-title">{t('classroom.sectionTitle')}</h2>

            {/* 文章列表 */}
            <Row gutter={[16, 16]}>
              {currentArticles.map((article) => (
                <Col key={article.id} xs={24} sm={12} lg={8} xl={6}>
                  <Card className="article-card h-100">
                    <h3 className="article-title">{article.title}</h3>
                    <p className="article-subtitle">{article.subtitle}</p>
                    <div className="mt-auto">
                      <Button
                        type="primary"
                        className="read-more-btn btn-primary-unified"
                        onClick={() => handleArticleClick(article)}
                      >
                        {t('classroom.readMore')}
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* 分页 */}
            <div className="pagination-container">
              <Pagination
                current={currentPage}
                total={articles.length}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) => t('classroom.pagination.showTotal')
                  .replace('{start}', range[0])
                  .replace('{end}', range[1])
                  .replace('{total}', total)}
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ClassroomPage;
